/**
 * Decides whether the visual-editing overlay should run on this page load.
 *
 * @nuxtjs/sanity ties the overlay to `visualEditingState.enabled`, which means "this session
 * may see drafts" — derived during SSR from the httpOnly `sanity-preview-id` cookie. That is
 * the wrong question twice over, so this plugin corrects it in both directions.
 *
 * **Turning it off.** Draft mode persists until the editor exits it, so an editor browsing
 * the live site normally still has an authorised session — and would get clickable outlines
 * over the whole site. Overlays belong in Presentation, so we clear the flag outside it. The
 * session itself is untouched: drafts still render and the draft-mode bar still offers the
 * exit, because the bar reads the SSR verdict captured by sanityDraftMode.server.ts rather
 * than this flag.
 *
 * **Turning it on.** Two contexts arrive with the flag false even though they are Presentation:
 * a page served from the route-rule cache (see localeBasePathTagRules — cached HTML has no
 * preview state, no drafts and no stega), and Presentation's open-in-new-tab button, which
 * lands top-level at `?sanity-preview-perspective=drafts` rather than in an iframe. In both
 * cases we set the flag and refetch; the BFF authorises per request and is never cached, so
 * drafts and the stega the overlay needs come back.
 *
 * `enforce: 'pre'` is required — the module's plugin reads the flag once and bails, so
 * changing it afterwards has no effect either way.
 *
 * Granting nothing: draft content still requires the httpOnly cookie at the BFF, so a
 * stranger framing the site, or adding the query param, gets an overlay over published
 * content with nothing to highlight. Who may frame us is limited by `frame-ancestors`.
 */
export default defineNuxtPlugin({
  name: 'sanity-presentation-gate',
  enforce: 'pre',
  setup(nuxtApp) {
    const visualEditingState = useSanityVisualEditingState();

    if (!visualEditingState) {
      return;
    }

    const params = new URLSearchParams(window.location.search);

    // `sanity-no-overlays` is our own opt-out, used by the Studio's read-only preview pane so
    // a plain iframe doesn't sprout editable outlines.
    const inPresentation = !params.has('sanity-no-overlays') && (
      // The same test @sanity/presentation-comlink's isMaybePresentation() performs, inlined
      // to avoid importing a transitive dependency of the module.
      window.self !== window.top
      || !!window.opener
      // Presentation's open-in-new-tab: top level, no opener, but always carries this param.
      || params.has('sanity-preview-perspective')
    );

    if (!inPresentation) {
      visualEditingState.enabled = false;
      return;
    }

    // SSR already established preview — nothing to rescue.
    if (visualEditingState.enabled) {
      return;
    }

    visualEditingState.enabled = true;

    // Wait for mount so this doesn't race initial hydration. The cached payload holds
    // published content, so a refetch is exactly what's wanted here.
    nuxtApp.hook('app:mounted', () => {
      refreshNuxtData();
    });
  },
});
